import { useTheme } from '@mui/material/styles';
import useFormatAmount from 'app-components/hooks/privacyMode/useFormatAmount';
import usePrivacyMaskValue from 'app-components/hooks/privacyMode/usePrivacyMaskValue';
import React, { forwardRef, useImperativeHandle, useMemo, type JSX } from 'react';
import type { EChartsOption } from 'echarts';
import { buildSeriesBarChartOption } from 'utils/chart-options';
import type { SeriesChartDataItem } from 'utils/chart-options';

import EChartsBase from '../EChartsBase';
import useEChartsTheme from '../useEChartsTheme';

export type SeriesBarChartRef = {
  clearSelection: () => void;
};

export interface SeriesBarChartProps {
  chartData: SeriesChartDataItem[];
  average: number;
  isIncome?: boolean;
  colorOverride?: string;
  height?: number | string;
}

const SeriesBarChart = forwardRef<SeriesBarChartRef, SeriesBarChartProps>(function SeriesBarChart(
  { chartData, average, isIncome, colorOverride, height = '100%' },
  ref,
): JSX.Element | null {
  const theme = useTheme();
  const echartsTheme = useEChartsTheme();
  const formatAmount = useFormatAmount();
  const privacyMask = usePrivacyMaskValue();

  // This chart has no selectable state; expose a no-op to satisfy the shared ref contract.
  useImperativeHandle(ref, () => ({ clearSelection() {} }));

  const applyOpacity = theme.applyOpacityToHex ?? ((hex: string) => hex);

  const mainColor = colorOverride ?? (isIncome ? theme.palette.legend.income : theme.palette.legend.bill);

  let fadedColor: string;
  if (colorOverride) {
    fadedColor = applyOpacity(colorOverride, 0.3);
  } else if (isIncome) {
    fadedColor = applyOpacity(theme.palette.legendDim.income, 0.5);
  } else {
    fadedColor = applyOpacity(theme.palette.legendDim.bill, 0.5);
  }

  const option = useMemo(() => {
    if (!chartData?.length) return null;
    return buildSeriesBarChartOption(chartData, {
      theme: echartsTheme,
      formatAmount,
      privacyMask,
      average,
      mainColor,
      fadedColor,
    });
  }, [chartData, echartsTheme, formatAmount, privacyMask, average, mainColor, fadedColor]);

  if (!option) return null;

  return (
    <EChartsBase option={option as EChartsOption} height={height} renderer='canvas' testId='echart-series-bar-chart' />
  );
});

export default SeriesBarChart;
